import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import api from '../../services/api';

import { useParams, useNavigate, Link } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  FaEye,
  FaRedo,
  FaEdit,
  FaTrashAlt,
  FaTags,
  FaPlus,
  FaSearch,
  FaFilter,
  FaSort,
  FaStar,
  FaTrash,
  FaTicketAlt,
  FaEnvelope,
  FaSpinner,
} from 'react-icons/fa';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from '../restaurants/Pagination';
import ConfirmationDialog from '../../components/restaurants/ConfirmationDialog';

// const api = axios.create({
//   baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
//   timeout: 5000,
// });

const TextMessagesPage = React.memo(() => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [textMessages, setTextMessages] = useState([]);
  const [savedMessages, setSavedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    currentPage: 1,
    messagesPerPage: 10,
    searchTerm: '',
    statusFilter: 'all',
    savedFilter: 'all',
    dateFilter: { start: null, end: null },
    sortField: 'scheduledDate',
    sortDirection: 'desc',
    recurrenceFilter: 'all',
    hasCouponFilter: 'all',
  });
  const [filtersOpen, setFiltersOpen] = useState(false); // State for collapsible drawer
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deleteAction, setDeleteAction] = useState(null);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ownerToken');
      const response = await api.get(`/restaurants/${restaurantId}/messages`, {
        params: {
          page: filters.currentPage,
          limit: filters.messagesPerPage,
          sortBy: filters.sortField,
          sortOrder: filters.sortDirection,
          status: filters.statusFilter,
          search: filters.searchTerm,
          startDate: filters.dateFilter.start,
          endDate: filters.dateFilter.end,
          saved: filters.savedFilter === 'saved' ? true : undefined,
          recurrence:
            filters.recurrenceFilter !== 'all'
              ? filters.recurrenceFilter
              : undefined,
          hasCoupon:
            filters.hasCouponFilter !== 'all'
              ? filters.hasCouponFilter
              : undefined,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setTextMessages(response.data.messages);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalMessages: response.data.totalMessages,
      });
      setError(null);
    } catch (err) {
      setError('Failed to fetch messages. Please try again.');
      toast.error('Failed to fetch messages. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, filters]);

  const fetchSavedMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      const response = await api.get(
        `/restaurants/${restaurantId}/messages/saved`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSavedMessages(response.data);
    } catch (error) {
      console.error('Error fetching saved messages:', error);
      toast.error('Failed to fetch saved messages. Please try again.');
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchMessages();
    fetchSavedMessages();
  }, [fetchMessages, fetchSavedMessages]);

  const handleSaveMessage = useCallback(
    async (messageId) => {
      try {
        const token = localStorage.getItem('ownerToken');
        await api.post(
          `/restaurants/${restaurantId}/messages/${messageId}/save`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        fetchSavedMessages();
        fetchMessages();
        toast.success('Message saved successfully');
      } catch (error) {
        console.error(
          'Error saving message:',
          error.response?.data || error.message
        );
        toast.error('Failed to save message. Please try again.');
      }
    },
    [restaurantId, fetchSavedMessages, fetchMessages]
  );

  const handleUnsaveMessage = useCallback(
    async (messageId) => {
      try {
        const token = localStorage.getItem('ownerToken');
        await api.post(
          `/restaurants/${restaurantId}/messages/${messageId}/unsave`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        fetchSavedMessages();
        fetchMessages();
        toast.success('Message unsaved successfully');
      } catch (error) {
        console.error('Error unsaving message:', error);
        toast.error('Failed to unsave message. Please try again.');
      }
    },
    [restaurantId, fetchSavedMessages, fetchMessages]
  );

  const handleSoftDelete = useCallback(
    async (messageId) => {
      try {
        const token = localStorage.getItem('ownerToken');
        await api.delete(`/restaurants/${restaurantId}/messages/${messageId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchMessages();
        toast.success('Message deleted successfully');
      } catch (error) {
        console.error('Error soft deleting text message', error);
        toast.error('Failed to delete message. Please try again.');
      }
    },
    [restaurantId, fetchMessages]
  );

  const handleSearchChange = debounce((value) => {
    setFilters((prev) => ({ ...prev, searchTerm: value, currentPage: 1 }));
  }, 300);

  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
      currentPage: filterName === 'currentPage' ? value : 1,
    }));
  };

  const resetFilters = () => {
    setFilters({
      currentPage: 1,
      messagesPerPage: 10,
      searchTerm: '',
      statusFilter: 'all',
      savedFilter: 'all',
      dateFilter: { start: null, end: null },
      sortField: 'scheduledDate',
      sortDirection: 'desc',
    });
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, currentPage: newPage }));
  };

  useEffect(() => {
    if (error) {
      // Redirect to error page
      navigate('/error', { state: { message: error } });
    }
  }, [error, navigate]);

  const handleSelectMessage = (messageId) => {
    setSelectedMessages((prev) =>
      prev.includes(messageId)
        ? prev.filter((id) => id !== messageId)
        : [...prev, messageId]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedMessages(memoizedTextMessages.map((message) => message._id));
    } else {
      setSelectedMessages([]);
    }
  };

  const deleteSelectedMessages = async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      console.log(selectedMessages);
      await api.post(
        `/restaurants/${restaurantId}/messages/delete-multiple`,
        { messageIds: selectedMessages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Selected messages deleted successfully');
      fetchMessages();
      setSelectedMessages([]);
    } catch (error) {
      toast.error('Failed to delete selected messages');
    }
  };

  // const handleDeleteClick = (messageId) => {
  //   setMessageToDelete(messageId);
  //   setShowConfirmDialog(true);
  // };
  const handleDeleteClick = (messageId) => {
    setDeleteAction({ type: 'single', id: messageId });
    setShowConfirmDialog(true);
  };

  const handleDeleteSelectedClick = () => {
    if (selectedMessages.length > 0) {
      setDeleteAction({ type: 'multiple', ids: selectedMessages });
      setShowConfirmDialog(true);
    } else {
      toast.warn('No messages selected');
    }
  };

  // const handleConfirmDelete = async () => {
  //   try {
  //     const token = localStorage.getItem('ownerToken');
  //     await api.delete(
  //       `/restaurants/${restaurantId}/messages/${messageToDelete}`,
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //       }
  //     );
  //     toast.success('Message deleted successfully');
  //     fetchMessages();
  //   } catch (error) {
  //     toast.error('Failed to delete message');
  //   } finally {
  //     setShowConfirmDialog(false);
  //     setMessageToDelete(null);
  //   }
  // };
  const handleConfirmDelete = async () => {
    try {
      const token = localStorage.getItem('ownerToken');
      if (deleteAction.type === 'single') {
        await api.delete(
          `/restaurants/${restaurantId}/messages/${deleteAction.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success('Message deleted successfully');
      } else if (deleteAction.type === 'multiple') {
        await api.post(
          `/restaurants/${restaurantId}/messages/delete-multiple`,
          {
            messageIds: deleteAction.ids,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success('Selected messages deleted successfully');
      }
      fetchMessages();
      setSelectedMessages([]);
    } catch (error) {
      toast.error('Failed to delete message(s)');
    } finally {
      setShowConfirmDialog(false);
      setDeleteAction(null);
    }
  };

  const memoizedTextMessages = useMemo(() => textMessages, [textMessages]);

  if (error || loading) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <FaSpinner className='animate-spin text-4xl text-blue-500' />
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8'>
      {/* <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      /> */}
      <div className='flex justify-between items-center mb-6'>
        <h1 className='text-3xl font-bold'>Text Messages </h1>
        <button
          className='bg-blue-600 text-white px-5 py-5 rounded-full hover:bg-blue-800 transition duration-300 ease-in-out shadow-md flex items-center'
          onClick={() =>
            navigate(`/restaurant/${restaurantId}/text-messages/new`)
          }
        >
          <FaPlus />
          {/* Create */}
        </button>
      </div>

      <div className='mb-4 relative'>
        <input
          type='text'
          placeholder='Search messages...'
          className='w-full pl-12 pr-4 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <FaSearch className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400' />
      </div>

      <div className='mb-4 flex flex-wrap items-center gap-4'>
        <div className='flex items-center'>
          <FaFilter className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleFilterChange('statusFilter', e.target.value)}
            value={filters.statusFilter}
          >
            <option value='all'>All Statuses</option>
            <option value='scheduled'>Scheduled</option>
            <option value='sent'>Sent</option>
            <option value='failed'>Failed</option>
          </select>
        </div>

        <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>Saved:</label>
          <select
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleFilterChange('savedFilter', e.target.value)}
            value={filters.savedFilter}
          >
            <option value='all'>All Messages</option>
            <option value='saved'>Saved Only</option>
          </select>
        </div>

        <div className='flex items-center'>
          <FaFilter className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('recurrenceFilter', e.target.value)
            }
            value={filters.recurrenceFilter}
          >
            <option value='all'>All Recurrences</option>
            <option value='none'>None</option>
            <option value='daily'>Daily</option>
            <option value='weekly'>Weekly</option>
            <option value='monthly'>Monthly</option>
          </select>
        </div>

        <div className='flex items-center'>
          <FaFilter className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('hasCouponFilter', e.target.value)
            }
            value={filters.hasCouponFilter}
          >
            <option value='all'>All Messages</option>
            <option value='with'>With Coupon</option>
            <option value='without'>Without Coupon</option>
          </select>
        </div>

        <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>Scheduled (From):</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('dateFilter', {
                ...filters.dateFilter,
                start: e.target.value,
              })
            }
            value={filters.dateFilter.start || ''}
          />
        </div>

        <div className='flex items-center'>
          <label className='whitespace-nowrap mr-2'>Scheduled (To):</label>
          <input
            type='date'
            className='p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) =>
              handleFilterChange('dateFilter', {
                ...filters.dateFilter,
                end: e.target.value,
              })
            }
            value={filters.dateFilter.end || ''}
          />
        </div>

        <div className='flex items-center'>
          <FaSort className='text-gray-600' />
          <select
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onChange={(e) => handleFilterChange('sortField', e.target.value)}
            value={filters.sortField}
          >
            <option value='createdAt'>Sort by Creation Date</option>
            <option value='scheduledDate'>Sort by Scheduled Date</option>

            <option value='title'>Sort by Title</option>
            {/* <option value='scheduledDate'>Sort by Date</option> */}
            <option value='status'>Sort by Status</option>
          </select>
          <button
            className='ml-2 p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            onClick={() =>
              handleFilterChange(
                'sortDirection',
                filters.sortDirection === 'asc' ? 'desc' : 'asc'
              )
            }
          >
            {filters.sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <button
          onClick={resetFilters}
          className='bg-red-500 text-white px-6 py-2 rounded hover:bg-red-700 transition duration-300 ease-in-out shadow-md'
        >
          Clear All Filters
        </button>
      </div>

      <div className='mb-4 flex justify-between items-center'>
        <div>
          <label className='inline-flex items-center'>
            <input
              type='checkbox'
              checked={selectedMessages.length === memoizedTextMessages.length}
              onChange={handleSelectAll}
              className='mr-2'
            />
            <span>Select All</span>
          </label>
        </div>
        <div>
          <span className='mr-4'>
            Selected: {selectedMessages.length} out of{' '}
            {memoizedTextMessages.length}
          </span>
          {/* <button
            onClick={deleteSelectedMessages}
            disabled={selectedMessages.length === 0}
            className='bg-red-500 text-white px-4 py-2 rounded disabled:opacity-50'
          >
            Delete Selected
          </button> */}
          <button
            onClick={handleDeleteSelectedClick}
            disabled={selectedMessages.length === 0}
            className='bg-red-500 text-white px-4 py-2 rounded disabled:opacity-50'
          >
            Delete Selected
          </button>
        </div>
      </div>

      {error ? (
        <div className='text-red-500 text-xl text-center'>{error}</div>
      ) : loading ? (
        <div className='flex justify-center items-center'>
          <div className='animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500'></div>
        </div>
      ) : (
        <div className='mt-6'>
          {memoizedTextMessages.length > 0 ? (
            <div className='grid grid-cols-1 gap-4'>
              {memoizedTextMessages.map((message) => (
                <MessageCard
                  key={message._id}
                  message={message}
                  savedMessages={savedMessages}
                  onSave={handleSaveMessage}
                  onUnsave={handleUnsaveMessage}
                  // onDelete={handleSoftDelete}
                  onDelete={() => handleDeleteClick(message._id)}
                  restaurantId={restaurantId}
                  isSelected={selectedMessages.includes(message._id)}
                  onSelect={() => handleSelectMessage(message._id)}
                />
              ))}
            </div>
          ) : (
            // <p className='text-center'>
            //   No text messages found for this restaurant.
            // </p>
            <div className='text-center py-10'>
              <FaEnvelope className='text-6xl text-gray-300 mx-auto mb-4' />
              <p className='text-xl text-gray-500'>No text messages found.</p>
              <p className='text-gray-400 mt-2'>
                Create new SMS message to get started!
              </p>
            </div>
          )}
        </div>
      )}

      {/* <div className='flex justify-center mt-5'>
        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
          (number) => (
            <button
              key={number}
              onClick={() => handleFilterChange('currentPage', number)}
              className={`mx-1 px-3 py-1 rounded ${
                pagination.currentPage === number
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {number}
            </button>
          )
        )}
      </div>

      <div className='flex justify-between items-center mt-4'>
        <div>
          <span className='mr-2'># per page:</span>
          <select
            value={filters.messagesPerPage}
            onChange={(e) =>
              handleFilterChange('messagesPerPage', Number(e.target.value))
            }
            className='p-2 border rounded'
          >
            <option value='5'>5</option>
            <option value='10'>10</option>
            <option value='20'>20</option>
            <option value='50'>50</option>
          </select>
        </div>

        <div>
          <span>
            Showing {(pagination.currentPage - 1) * filters.messagesPerPage + 1}{' '}
            -{' '}
            {Math.min(
              pagination.currentPage * filters.messagesPerPage,
              pagination.totalMessages
            )}{' '}
            of {pagination.totalMessages}
          </span>
        </div>
      </div> */}

      {!loading && !error && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={filters.messagesPerPage}
          totalItems={pagination.totalCoupons}
        />
      )}

      <div className='flex justify-between items-center mt-4'>
        <div>
          <span className='mr-2'># per page:</span>
          <select
            value={filters.messagesPerPage}
            onChange={(e) =>
              handleFilterChange('messagesPerPage', Number(e.target.value))
            }
            className='p-2 border rounded'
          >
            {/* <option value='1'>1</option> */}
            <option value='5'>5</option>
            <option value='10'>10</option>
            <option value='20'>20</option>
            <option value='50'>50</option>
          </select>
        </div>

        <div>
          <span>
            Showing {(pagination.currentPage - 1) * filters.messagesPerPage + 1}{' '}
            -{' '}
            {Math.min(
              // pagination.currentPage * filters.couponsPerPage,
              // pagination.totalCoupons
              pagination.currentPage * filters.messagesPerPage,
              pagination.totalMessages
            )}{' '}
            of {pagination.totalMessages}
            {/* {pagination.totalCoupons} */}
          </span>
        </div>
      </div>
      {/* <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmDelete}
        title='Confirm Deletion'
        message='Are you sure you want to delete this message? This action cannot be undone.'
      /> */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmDelete}
        title='Confirm Deletion'
        message={
          deleteAction?.type === 'single'
            ? 'Are you sure you want to delete this message? This action cannot be undone.'
            : `Are you sure you want to delete ${deleteAction?.ids.length} selected messages? This action cannot be undone.`
        }
      />
    </div>
  );
});

const MessageCard = React.memo(
  ({
    message,
    savedMessages,
    onSave,
    onUnsave,
    onDelete,
    restaurantId,
    isSelected,
    onSelect,
  }) => (
    <div
      className={`bg-white shadow-lg p-4 rounded relative ${
        isSelected ? 'border-2 border-blue-500' : ''
      } cursor-pointer hover:shadow-xl transition-shadow duration-300`}
      onClick={(e) => {
        if (
          e.target.type !== 'checkbox' &&
          e.target.tagName !== 'BUTTON' &&
          e.target.tagName !== 'A'
        ) {
          onSelect();
        }
      }}
    >
      <input
        type='checkbox'
        checked={isSelected}
        onChange={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        className='absolute top-2 right-2'
      />
      {/* <button
        onClick={() =>
          savedMessages.some((saved) => saved._id === message._id)
            ? onUnsave(message._id)
            : onSave(message._id)
        }
        className='absolute top-6 right-2 text-yellow-500 hover:text-yellow-600'
        title={
          savedMessages.some((saved) => saved._id === message._id)
            ? 'Unsave'
            : 'Save'
        }
      >
        <FaStar
          className={
            savedMessages.some((saved) => saved._id === message._id)
              ? 'text-yellow-500'
              : 'text-gray-300'
          }
          size={24}
        />
      </button> */}
      <h2 className='text-lg font-semibold'>{message.title}</h2>
      <p className='text-sm font-semibold text-gray-600'>{message.content}</p>
      <p className='text-sm text-gray-500'>
        Scheduled: {new Date(message.scheduledDate).toLocaleString()}
      </p>
      <p className='text-sm text-gray-500'>
        Created: {new Date(message.createdAt).toLocaleString()}
      </p>
      <p className='text-sm text-gray-500'>Status: {message.status}</p>
      <p className='text-sm text-gray-500'>
        Recipients: {message.recipients.length}
      </p>
      <p className='text-sm text-gray-500'>Recurrence: {message.recurrence}</p>
      {message.imageUrl && (
        <p className='text-sm text-gray-500'>
          Image:
          <Link to={message.imageUrl} className='hover:text-blue-600'>
            {' '}
            {message.imageUrl}
          </Link>
        </p>
      )}
      <div className='mt-4 flex flex-wrap gap-1'>
        {message.status === 'sent' || message.status === 'failed' ? (
          <>
            <Link
              to={`/restaurant/${restaurantId}/messages/${message._id}/edit/view`}
              className='bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded content-center'
            >
              {/* View */}
              <FaEye className='text-lg text-center' />
            </Link>
            <Link
              to={`/restaurant/${restaurantId}/messages/${message._id}/edit/resend`}
              className='bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded content-center'
            >
              <FaEnvelope className='text-lg text-center' />

              {/* Send Again */}
            </Link>
          </>
        ) : (
          <Link
            to={`/restaurant/${restaurantId}/messages/${message._id}/edit/resend`}
            className='bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded content-center'
          >
            {/* Edit */}
            <FaEdit className='text-lg text-center' />
          </Link>
        )}

        {message.messageType === 'coupon' &&
          message.status !== 'failed' &&
          message.recurrence === 'none' && (
            <Link
              to={`/restaurant/${restaurantId}/messages/${message._id}/coupons`}
              className='bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded content-center'
            >
              {/* Coupons */}
              <FaTicketAlt className='text-lg text-center' />
            </Link>
          )}
        {/* <button
          onClick={() => onDelete(message._id)}
          className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
        >
          <FaTrash className='text-center ' />

        </button> */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(message._id);
          }}
          className='bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded'
        >
          <FaTrash className='text-center' />
        </button>

        <button
          onClick={() =>
            savedMessages.some((saved) => saved._id === message._id)
              ? onUnsave(message._id)
              : onSave(message._id)
          }
          className='bg-gray-500 hover:bg-gray-700 text-yellow-500 hover:text-yellow-600 py-2 px-4 rounded'
          title={
            savedMessages.some((saved) => saved._id === message._id)
              ? 'Unsave'
              : 'Save'
          }
        >
          <FaStar
            className={
              savedMessages.some((saved) => saved._id === message._id)
                ? 'text-yellow-500  text-center'
                : 'text-gray-300 text-center'
            }
            size={24}
          />
        </button>
      </div>
    </div>
  )
);

export default TextMessagesPage;
